// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable max-lines */
import React, { useCallback, useMemo, useState } from 'react'
import {
  Box, ShowIfDesktop, ShowIfNotDesktop, Text, useResponsiveWrapperContext,
} from '@usap/uicomponents'
import { PopUpLayer, StaticData } from '@usap/widgets'
import commaSeparetedValue from '../../../../../../helpers/commaSeparetedValue'
import useUpdateWarrranty from './hooks/useUpdateWarranty'
import AddProtectionPlan from './AddProtectionPlan'
import useWmoEnabled from '../../../../../../hooks/useWmoEnabled'
import WarrantyWmo from './WarrantyWmo'
import { GTM } from '../../../../../../helpers';

// eslint-disable-next-line import/exports-last
export const WARRANTY_TYPE = {
  LIFETIME: 'lifetime',
  ONE_YEAR: '1_year',
  THREE_YEAR: '3_year',
  FIVE_YEAR: '5_year',
}

const WARRANTY_TERMS = {
  [WARRANTY_TYPE.LIFETIME]: 'Lifetime',
  [WARRANTY_TYPE.ONE_YEAR]: '1 year',
  [WARRANTY_TYPE.THREE_YEAR]: '3 years',
  [WARRANTY_TYPE.FIVE_YEAR]: '5 years',
}

const mobileDataTestId = {
  protectionPlanCheckbox: 'wom-lpp-checkbox',
  whatsThis: 'wom-lpp-whats-this',
}

const desktopDataTestId = {
  protectionPlanCheckbox: 'desk-lpp-checkbox',
  whatsThis: 'desk-lpp-whats-this',
}

const Warranty = props => {
  const {
    data, tooltip, renderCustomAddProtectionPlanComponent = null, page,
  } = props
  const [showTooltip, shouldShowTooltip] = useState(false)
  const { isDesktop } = useResponsiveWrapperContext()
  const isWmoEnabled = useWmoEnabled()

  const style = useMemo(
    () => ({
      wrapper: {
        order: isDesktop ? 2 : 1,
        marginTop: isDesktop ? 0 : 8,
        borderTop: isDesktop ? 'none' : 'solid 1px #E0E0E0',
      },
      checkBoxWrapperMargin: { bottom: 'medium' },
      labelTextMargin: { vertical: 'small' },
      tooltipTextMargin: { left: 'xsmall' },
    }),
    [isDesktop],
  )

  const { modifiers, warranty } = data
  const quantity = modifiers.quantity || 0
  let price = 0
  let totalPrice = 0
  let type = ''

  if (warranty) {
    price = warranty.customerProtectionPlan.price
    totalPrice = parseFloat(price) * quantity
    type = warranty.customerProtectionPlan.type
      .toUpperCase()
    type = type.replace('_', ' ')
  }
  const formattedTotalPrice = commaSeparetedValue(totalPrice)
  const formattedPrice = commaSeparetedValue(price)

  const warrantyType = warranty?.customerProtectionPlan.type
  const warrantyText = useMemo(() => {
    if (!warrantyType || warrantyType === WARRANTY_TYPE.LIFETIME || !tooltip?.warrantyCustom || !tooltip.warranty) {
      return tooltip?.warranty || ''
    }
    const regex = new RegExp('%TERMS%', 'g');
    return tooltip?.warrantyCustom?.replace(regex, WARRANTY_TERMS[`${warrantyType}`]) || ''
  }, [tooltip, warrantyType])

  const priceReplace = `$${formattedTotalPrice}`
  const termsReplace = WARRANTY_TERMS[`${warrantyType}`]
  const replaceKeywords = useCallback(str => str?.replaceAll('%TERMS%', termsReplace)?.replaceAll('%PRICE%', priceReplace) || '',
    [priceReplace, termsReplace])

  const warrantyData = useMemo(() => {
    if (!warrantyType || !tooltip.warrantyWmo) {
      return null
    }

    const heading = replaceKeywords(tooltip.warrantyWmo.heading)
    const subtitle = replaceKeywords(tooltip.warrantyWmo.subtitle)
    const items = (tooltip.warrantyWmo.items || []).map(c => {
      const title = replaceKeywords(c.title)
      const content = replaceKeywords(c.content)
      return { title, content }
    })
    return { heading, subtitle, items }
  }, [replaceKeywords, tooltip.warrantyWmo, warrantyType])

  const isChecked = useMemo(
    () => {
      if (modifiers.warranty) return true
      return false
    },
    [modifiers],
  )

  const updateWarranty = useUpdateWarrranty({ data })

  const handleWarranty = useCallback(
    async event => {
      await updateWarranty(event.target.checked)
    },
    [updateWarranty],
  )

  const sku = data?.product?.sku
  const partName = data?.product?.part?.name 
  const handleShowTooltip = useCallback(
    () => {
      GTM.cppLearnMoreModalClicked(sku, partName)
      shouldShowTooltip(true)
    },
    [shouldShowTooltip],
  )

  const isCart = page === 'Cart'

  if (price === 0) {
    return <></>
  }

  if (isCart && isWmoEnabled) {
    return (
      <WarrantyWmo
        showTooltip={showTooltip}
        warrantyText={warrantyText}
        sku={sku}
        partName={partName}
        warrantyData={warrantyData}
        shouldShowTooltip={shouldShowTooltip}
        StaticData={StaticData}
        type={type}
        formattedTotalPrice={formattedTotalPrice}
        quantity={quantity}
        formattedPrice={formattedPrice}
        handleWarranty={handleWarranty}
        handleShowTooltip={handleShowTooltip}
        isChecked={isChecked}
        dataTestId={isDesktop ? desktopDataTestId : mobileDataTestId}
      />

    )
  }

  return (
    <Box
      style={style.wrapper}
    >
      <PopUpLayer
        flag={showTooltip}
        records={warrantyText}
        closePopUp={shouldShowTooltip}
        PopUpComponent={StaticData}
      />

      {renderCustomAddProtectionPlanComponent
        ? renderCustomAddProtectionPlanComponent({
          warrantyType, handleWarranty, isChecked, handleShowTooltip, totalPrice, price, quantity,
        })
        : (
          <>
            <Text
              size="small"
              weight="bold"
              color="dark-1"
              margin={style.labelTextMargin}
            >
              {`${type} PROTECTION PLAN FOR $${formattedTotalPrice}`}
              {(quantity && quantity > 1) && ` ($${formattedPrice} each)`}
            </Text>
            <ShowIfDesktop>
              <AddProtectionPlan
                customStyle={style}
                handleWarranty={handleWarranty}
                isChecked={isChecked}
                handleShowTooltip={handleShowTooltip}
                dataTestId={desktopDataTestId}
              />
            </ShowIfDesktop>
            <ShowIfNotDesktop>
              <AddProtectionPlan
                customStyle={style}
                handleWarranty={handleWarranty}
                isChecked={isChecked}
                handleShowTooltip={handleShowTooltip}
                dataTestId={mobileDataTestId}
              />
            </ShowIfNotDesktop>
          </>
        )}
    </Box>
  )
}

export default Warranty
